import { sendToAnalytics } from "lib/googleAnalytics";

import {
  shouldSendAnalytics,
  matchRGBstringToColorId,
  convertValueToUint16,
  hexToRgb,
  getRGBarray,
  mapRGBtoTableColors,
} from "utils/utils";
import * as DeviceStates from "./DeviceStates";
import { onGattServerDisconnected } from "lib/deviceReconnectingNotification";
import image from "images/devices/SpotW1-promotion.png";
import { LIGHT_SHOWS_KEYS } from "data/constants";

const names = ["Spot W1"];

const SPOT_W1_SERVICE_UUID = 0x1400;
const ACTUATOR_MOTOR_UUID = 0x1401;

const BATTERY_SERVICE_UUID = 0x180f;
const BATTERY_LEVEL_UUID = 0x2a19;

const LED_CONTROL_UUID = 0x1402;
const CHANGE_NAME_UUID = 0x1403;

const DEVICE_INFO_SERVICE_UUID = "180A";
const DEVICE_SPECIFIC_INFO = 0x2a29;

const PERIOD_DURATION = 1000; // ms; default value for non-constant vibration mode
const DEFAULT_MAX_LIGHT_INTENSITY = 250;

/**
 * Abstract Class BaseDeviceWrapper.
 * @class BaseDeviceWrapper
 *
 * Device wrapper over the Web Bluetooth device object
 * Responsible for
 * - holding the WebBluetooth object, its service and characteristics
 * - Connecting and disconnecting to the Web Bluetooth device
 * - Discovering its service and characteristic during the connection process
 * - holding Web Bluetooth device connection state (connected, disconnected, connecting)
 * - Handling Web Bluetooth device disconnection
 *
 * @collab Is a base class for concrete device classes like Pearl2, Cliona, etc.
 * @param {obj} webBleDevice - Web Bluetooth device object
 */
export default class SpotW1 {
  constructor(device) {
    this.device = device;
    this.image = image;
    this.server = null;

    this.primaryService = null;
    this.actuatorCharacteristic = null;
    this.ledCharacteristic = null;
    this.overrideNameCharacteristic = null;

    this.batteryService = null;
    this.batteryCharacteristic = null;

    this.connectionState = DeviceStates.DISCONNECTED;
    this.connectionStateCallback = null;
    this.defaultDeviceName = "Spot W1";

    device.addEventListener("gattserverdisconnected", () => {
      onGattServerDisconnected(this);
    });
  }

  static get deviceNames() {
    return names;
  }

  static get services() {
    return [SPOT_W1_SERVICE_UUID, BATTERY_SERVICE_UUID];
  }

  get companyName() {
    return "Kiiroo";
  }

  /**
   * Set new connection state and notify the callback
   * @param {string} newState - new state from DeviceStates.*
   */
  setConnectionState(newState) {
    this.connectionState = newState;
    this.connectionStateCallback?.();
  }

  /**
   * Connect to the device and discover the service and characteristic
   */
  async connect() {
    async function innerConnect() {
      const startTime = performance.now();

      this.setConnectionState(DeviceStates.CONNECTING);
      this.server = await this.device.gatt.connect();

      // motor
      this.primaryService = await this.server.getPrimaryService(
        SPOT_W1_SERVICE_UUID
      );
      this.actuatorCharacteristic = await this.primaryService.getCharacteristic(
        ACTUATOR_MOTOR_UUID
      );

      // battery
      this.batteryService = await this.server.getPrimaryService(
        BATTERY_SERVICE_UUID
      );
      this.batteryCharacteristic = await this.batteryService.getCharacteristic(
        BATTERY_LEVEL_UUID
      );

      // LEDs
      this.ledCharacteristic = await this.primaryService.getCharacteristic(
        LED_CONTROL_UUID
      );

      // override name
      this.overrideNameCharacteristic =
        await this.primaryService.getCharacteristic(CHANGE_NAME_UUID);

      this.setConnectionState(DeviceStates.CONNECTED);
      clearTimeout(this.connectionTimeout);

      // Report to google analytics
      const milliseconds = Math.round(performance.now() - startTime);
      sendToAnalytics(
        "device_connect",
        "device",
        this.defaultDeviceName,
        milliseconds
      );
    }

    return await Promise.race([innerConnect.bind(this)()]);
  }

  /**
   * Disconnect from the device
   */
  async disconnect() {
    this.setConnectionState(DeviceStates.DISCONNECTING);
    if (this.device.gatt.connected) {
      await this.device.gatt.disconnect();
    }
    this.setConnectionState(DeviceStates.MANUALLY_DISCONNECTED);
    sendToAnalytics("device_disconnect", "device", this.defaultDeviceName);
  }

  async getBattery() {
    const value = await this.batteryCharacteristic.readValue();
    return value.getUint8(0);
  }

  // working only on Windows
  async setDeviceName(newName) {
    const hexDeviceName = newName.split("").map((c) => c.charCodeAt(0));
    await this.overrideNameCharacteristic.writeValue(
      new Uint8Array(hexDeviceName)
    );
  }

  // motor control
  async startMode(totalDuration, low, high, mode = 1, periodDuration = 500) {
    const data = [
      low || high ? mode : 0,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(periodDuration),
      low,
      high,
    ];
    await this.actuatorCharacteristic.writeValue(new Uint8Array(data));
  }

  async write(percent) {
    const STOP_VALUE = 0;
    const FOREVER_VIBRATE_VALUE = 255;

    const data = [
      0,
      percent ? FOREVER_VIBRATE_VALUE : STOP_VALUE,
      0,
      0,
      0,
      percent,
    ];

    await this.actuatorCharacteristic.writeValue(new Uint8Array(data));
  }

  async runStarlightEffect(totalDuration, primaryRgbColor) {
    const timePeriod = 250;

    const frameData = [
      9,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      matchRGBstringToColorId(primaryRgbColor),
      3, // White color
      3, // White color
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runCometEffect(totalDuration) {
    const timePeriod = 250;
    const frameData = [
      2,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      DEFAULT_MAX_LIGHT_INTENSITY, // Red channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Green channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Blue channel
    ];

    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runBlinkEffect(totalDuration, rgbColor) {
    const timePeriod = 200;
    const mappedRGB = mapRGBtoTableColors(rgbColor);

    const frameData = [
      1,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      ...getRGBarray(mappedRGB),
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runGlowEffect(totalDuration, rgbColor) {
    const mappedRGB = mapRGBtoTableColors(rgbColor);

    const frameData = [
      12,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(25),
      150,
      ...getRGBarray(mappedRGB),
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runSpinEffect(totalDuration, rgbColor) {
    const timePeriod = 100;
    const mappedRGB = mapRGBtoTableColors(rgbColor);

    const frameData = [
      4,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      ...getRGBarray(mappedRGB),
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runSpectrumEffect(totalDuration) {
    const timePeriod = 100;

    const frameData = [
      5,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      DEFAULT_MAX_LIGHT_INTENSITY, // Red channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Green channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Blue channel
    ];

    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runUnicornEffect(totalDuration) {
    const timePeriod = 200;

    const frameData = [
      11,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      DEFAULT_MAX_LIGHT_INTENSITY, // Red channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Green channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Blue channel
    ];

    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runFlareEffect(totalDuration) {
    const timePeriod = 250;

    const frameData = [
      10,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      DEFAULT_MAX_LIGHT_INTENSITY, // Red channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Green channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Blue channel
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runSparkleEffect(totalDuration, rgbColor) {
    const timePeriod = 250;
    const mappedRGB = mapRGBtoTableColors(rgbColor);

    const frameData = [
      7,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      ...getRGBarray(mappedRGB),
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runStrobeEffect(totalDuration, rgbColor) {
    const timePeriod = 250;
    const mappedRGB = mapRGBtoTableColors(rgbColor);

    const frameData = [
      6,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      ...getRGBarray(mappedRGB),
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runSupernovaEffect(totalDuration) {
    const frameData = [
      13,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(DEFAULT_MAX_LIGHT_INTENSITY * 25),
      DEFAULT_MAX_LIGHT_INTENSITY,
      0,
      0,
      0,
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runGalaxyEffect(totalDuration) {
    const timePeriod = 175;

    const frameData = [
      3,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      DEFAULT_MAX_LIGHT_INTENSITY, // Red channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Green channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Blue channel
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runRainbowEffect(totalDuration) {
    const timePeriod = 50;

    const frameData = [
      8,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      DEFAULT_MAX_LIGHT_INTENSITY, // Red channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Green channel
      DEFAULT_MAX_LIGHT_INTENSITY, // Blue channel
    ];

    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async runSteadyOnLights(totalDuration, rgbColor) {
    const timePeriod = 250;

    const mappedRGB = mapRGBtoTableColors(rgbColor);
    const frameData = [
      0,
      Math.round(totalDuration / 1000),
      ...convertValueToUint16(timePeriod),
      DEFAULT_MAX_LIGHT_INTENSITY,
      ...getRGBarray(mappedRGB),
    ];
    await this.ledCharacteristic.writeValue(new Uint8Array(frameData));
  }

  async playLightShow(
    duration,
    lightShow,
    lightColor,
    secondaryColor = "rgb(0,0,0)"
  ) {
    switch (lightShow) {
      case LIGHT_SHOWS_KEYS.Starlight:
        this.runStarlightEffect(duration, lightColor, secondaryColor);
        break;
      case LIGHT_SHOWS_KEYS.Comet:
        this.runCometEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Blink:
        this.runBlinkEffect(duration, lightColor);
        break;
      case LIGHT_SHOWS_KEYS.Glow:
        this.runGlowEffect(duration, lightColor);
        break;
      case LIGHT_SHOWS_KEYS.Spin:
        this.runSpinEffect(duration, lightColor);
        break;
      case LIGHT_SHOWS_KEYS.Spectrum:
        this.runSpectrumEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Unicorn:
        this.runUnicornEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Flare:
        this.runFlareEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Sparkle:
        this.runSparkleEffect(duration, lightColor);
        break;
      case LIGHT_SHOWS_KEYS.Strobe:
        this.runStrobeEffect(duration, lightColor);
        break;
      case LIGHT_SHOWS_KEYS.Supernova:
        this.runSupernovaEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Galaxy:
        this.runGalaxyEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Rainbow:
        this.runRainbowEffect(duration);
        break;
      case LIGHT_SHOWS_KEYS.Continuous:
        this.runSteadyOnLights(duration, lightColor);
        break;
      default:
        this.runRainbowEffect(duration);
        return;
    }
  }

  async processTipRange(totalDuration, intensity, lightShow, lightColor) {
    this.startMode(totalDuration, intensity, intensity);
    this.playLightShow(totalDuration, lightShow, hexToRgb(lightColor));
  }

  async processTipPattern(pattern) {
    const {
      mode,
      duration: totalDuration,
      intensity: maxIntensity,
      lightShow,
      lightColor,
      periodDuration = 1000,
    } = pattern;

    if (!pattern.intensitiesPerStep) {
      this.startMode(totalDuration, 0, maxIntensity, mode, periodDuration);
    }
    this.playLightShow(totalDuration, lightShow, hexToRgb(lightColor));
  }
}
